import { useEffect, useCallback, useMemo } from 'react'
import FeedView from 'components/FeedView'
import { Feed, Stream, AnyContent, RangeFilter, ContentItem } from 'interfaces'
import { FilterType } from 'shared'

export interface StreamFeedViewProps {
  feed: Feed
  streamId: string
  stream?: Stream
  filter: FilterType
  range: RangeFilter
  basePath: string
  linkToUserFeedId?: boolean
  itemWidth?: number
  onItemPinned?(stream: Stream, feed: Feed, content: AnyContent): void
  onItemShared?(stream: Stream, feed: Feed, content: AnyContent): void | boolean
  onGotoClicked?(stream: Stream, feed: Feed, content: AnyContent, by: string): void
  onFilterChanged?(stream: Stream, feed: Feed, filter: FilterType): void
  onRangeChanged?(stream: Stream, feed: Feed, range: RangeFilter): boolean
  onDistinctFeed?(stream: Stream, feed: Feed): void
  onContentItemClick?(item: ContentItem): void
}

export function StreamFeedView({
  feed,
  streamId,
  stream,
  filter,
  range,
  basePath,
  linkToUserFeedId,
  itemWidth,
  onItemPinned,
  onItemShared,
  onGotoClicked,
  onFilterChanged,
  onRangeChanged,
  onDistinctFeed,
  onContentItemClick
}: StreamFeedViewProps) {
  const currentStream = useMemo(
    () => stream || ({ id: streamId } as Stream),
    [stream, streamId]
  )

  const handleItemPinned = useCallback((feed: Feed, content: AnyContent) => {
    if (onItemPinned) {
      onItemPinned(currentStream, feed, content)
    }
  }, [onItemPinned, currentStream])

  const handleItemShared = useCallback((feed: Feed, content: AnyContent) => {
    if (onItemShared) {
      return onItemShared(currentStream, feed, content)
    }
  }, [onItemShared, currentStream])

  const handleFilterChanged = useCallback((feed: Feed, filter: FilterType) => {
    if (onFilterChanged) {
      onFilterChanged(currentStream, feed, filter)
    }
  }, [onFilterChanged, currentStream])

  const handleRangeChanged = useCallback((feed: Feed, range: RangeFilter) => {
    if (onRangeChanged) {
      return onRangeChanged(currentStream, feed, range)
    }
    return false
  }, [onRangeChanged, currentStream])

  const handleGotoClicked = useCallback((feed: Feed, content: AnyContent, by: string) => {
    if (onGotoClicked) {
      onGotoClicked(currentStream, feed, content, by)
    }
  }, [onGotoClicked, currentStream])

  // handle distinct feed logic on mount + updates
  useEffect(() => {
    if (feed && onDistinctFeed) {
      onDistinctFeed(currentStream, feed)
    }
  }, [feed, onDistinctFeed, currentStream])

  return (
    <FeedView
      feed={feed}
      itemWidth={itemWidth}
      defaultFilter={filter}
      range={range}
      linkToUserFeedId={linkToUserFeedId}
      basePath={basePath}
      onItemShared={handleItemShared}
      onItemPinned={handleItemPinned}
      onGotoClicked={handleGotoClicked}
      onFilterChanged={handleFilterChanged}
      onRangeChanged={handleRangeChanged}
      onContentItemClick={onContentItemClick}
    />
  )
}

export default StreamFeedView
